import { Box, Button, HStack, Text } from '@chakra-ui/react'
import { QueryDocumentSnapshot } from 'firebase/firestore'
import FirestoreUser from '../types/FirestoreUser'

interface AZSelectorProps {
  /** The currently loaded Firestore user docs to infer loaded initials */
  docs: QueryDocumentSnapshot<FirestoreUser>[]
  /** Which field to use when computing initials */
  field: 'firstName' | 'lastName' | 'email'
  /** Whether there are more pages to be loaded (affects disabling logic) */
  hasMore: boolean
  /** Optional callback for parent to handle letter clicks */
  onLetterClick?: (letter: string) => void
  /** Button size - defaults to 'xs' like existing code */
  buttonSize?: string
  /** Show field label for email disambiguation */
  showFieldLabel?: boolean
}

/**
 * AZSelector - Reusable A-Z navigation component
 *
 * Extracts initials from the provided docs and renders clickable A-Z buttons.
 * Letters are disabled when hasMore=true and that letter's data hasn't been loaded yet.
 *
 * Parent should handle onLetterClick by calling their table ref's scrollToLetter method
 * or implementing custom scroll/filter logic.
 */
export default function AZSelector({
  docs,
  field,
  hasMore,
  onLetterClick,
  buttonSize = 'xs',
  showFieldLabel = false,
}: AZSelectorProps) {
  // Compute loaded initials exactly as Users.tsx does
  const loadedInitials = new Set(
    docs
      .map((d) => {
        if (field === 'email') {
          const email = d.data().email as string | undefined
          return email ? email.charAt(0).toUpperCase() : undefined
        }
        // name fields
        const name =
          field === 'firstName'
            ? d.data().profile?.firstName
            : d.data().profile?.lastName
        return name as string | undefined
      })
      .filter(Boolean)
      .map((n) => n!.charAt(0).toUpperCase())
  )

  const maxLoadedInitial = Array.from(loadedInitials).sort().slice(-1)[0] || 'A'

  return (
    <Box
      position='sticky'
      top={0}
      zIndex={5}
      bg='white'
      borderTop='1px solid'
      borderBottom='1px solid'
      borderColor='gray.200'
      boxShadow='sm'
      py={2}
      px={1}
    >
      {/* Show field label when needed (e.g., for email disambiguation) */}
      {showFieldLabel && field === 'email' && (
        <Text
          fontSize='sm'
          color='gray.600'
          mb={1}
          ml={1}
          fontWeight='semibold'
        >
          A-Z (Email)
        </Text>
      )}

      <HStack spacing={1} flexWrap='wrap'>
        {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(
          (letter) => {
            const notYetLoaded = hasMore && letter > maxLoadedInitial
            return (
              <Button
                key={letter}
                size={buttonSize}
                variant='ghost'
                fontSize='lg'
                px={2}
                py={1}
                isDisabled={notYetLoaded}
                opacity={notYetLoaded ? 0.4 : 1}
                cursor={notYetLoaded ? 'not-allowed' : 'pointer'}
                onClick={() => {
                  if (notYetLoaded) return
                  onLetterClick?.(letter)
                }}
              >
                {letter}
              </Button>
            )
          }
        )}
      </HStack>
    </Box>
  )
}
